import { useState } from "react";
import { useMeters, useEvents } from "../hooks/useMeters";
import {
  PageHeader,
  MeterSelect,
  DaysSelect,
  Spinner,
  EmptyState,
} from "../components/common";
import { fmt } from "../utils/helpers";
import { Bell, ShieldAlert, Settings, ToggleLeft } from "lucide-react";
import Layout from "../../../layout";
import "../reportAnalytics.css";

const EVENT_META: Record<
  string,
  { label: string; icon: any; badge: string; iconBg: string }
> = {
  tamper_count: {
    label: "Tamper Alert",
    icon: ShieldAlert,
    badge: "ra-badge-red",
    iconBg: "bg-red-100",
  },
  program_count: {
    label: "Program Change",
    icon: Settings,
    badge: "ra-badge-blue",
    iconBg: "bg-blue-100",
  },
  md_reset_count: {
    label: "MD Reset",
    icon: Bell,
    badge: "ra-badge-yellow",
    iconBg: "bg-amber-100",
  },
  relay_status: {
    label: "Relay Change",
    icon: ToggleLeft,
    badge: "ra-badge-violet",
    iconBg: "bg-violet-100",
  },
};

export default function EventLogPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);
  const [filter, setFilter] = useState("all");

  const { data, isLoading } = useEvents(meterId || meters[0], days);
  const events = data?.data ?? [];
  const filtered =
    filter === "all"
      ? events
      : events.filter((e: any) => e.event_type === filter);

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Event Log"
          subtitle="Tamper, program, relay, and demand-reset events"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <select
            className="ra-select-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All events</option>
            <option value="tamper_count">Tamper only</option>
            <option value="program_count">Program only</option>
            <option value="md_reset_count">MD Reset only</option>
            <option value="relay_status">Relay only</option>
          </select>
          <DaysSelect value={days} onChange={setDays} options={[30, 60, 90]} />
        </PageHeader>

        <div className="ra-card">
          {isLoading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <EmptyState message="No events found for this meter and period" />
          ) : (
            <div className="space-y-2">
              {filtered.map((ev: any, i: number) => {
                const meta = EVENT_META[ev.event_type] ?? {
                  label: ev.description,
                  icon: Bell,
                  badge: "ra-badge-blue",
                  iconBg: "bg-blue-100",
                };
                const Icon = meta.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors"
                  >
                    <div className={`mt-0.5 p-1.5 rounded-lg ${meta.iconBg}`}>
                      <Icon size={14} className="text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={meta.badge}>{meta.label}</span>
                        <span className="text-xs text-slate-500 font-mono">
                          {ev.meter_id}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {ev.previous_value} →{" "}
                        <span className="text-slate-800 font-semibold">
                          {ev.new_value}
                        </span>
                      </p>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0 font-mono">
                      {fmt.dt(ev.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
